import { fileRefUrl } from './file-ref.js';

/**
 * Pure BlockNote→Markdown serialization for prompt assembly and embedding chunks.
 * No BlockNote import, no DOM — runs on server, client, and worker (07-chat.md).
 *
 * A stored block's `content` jsonb is `{ props, content }`, where the inner `content`
 * is BlockNote inline content: an array of text/link nodes, a tableContent object, or
 * undefined. Unknown shapes degrade to best-effort text and never throw.
 */

export type StoredBlockContent = {
  props?: Record<string, unknown> | null;
  content?: unknown;
};

export type SerializableBlock = {
  type: string;
  title?: string | null;
  content?: StoredBlockContent | Record<string, unknown> | null;
  /** Nesting depth for list/indentation (0 = top level). */
  depth?: number;
};

/** `display` keeps filenames. `stable` writes `cs-file://<fileId>` so a later replace keeps the file. */
export type MarkdownFileRefs = 'display' | 'stable';

export type MarkdownSerializeOptions = {
  fileRefs?: MarkdownFileRefs;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readProps(content: SerializableBlock['content']): Record<string, unknown> {
  if (!isRecord(content)) {
    return {};
  }
  if (isRecord(content.props)) {
    return content.props as Record<string, unknown>;
  }
  // Older rows may store props at the top level (see blockContentToStored on the client).
  if (!('content' in content)) {
    return content as Record<string, unknown>;
  }
  return {};
}

function readInline(content: SerializableBlock['content']): unknown {
  if (!isRecord(content)) {
    return undefined;
  }
  return content.content;
}

/** Concatenate the visible text of BlockNote inline content. Links use their inner text. */
export function inlineToText(inline: unknown): string {
  if (typeof inline === 'string') {
    return inline;
  }
  if (Array.isArray(inline)) {
    return inline.map(inlineToText).join('');
  }
  if (!isRecord(inline)) {
    return '';
  }
  if (inline.type === 'text' && typeof inline.text === 'string') {
    return inline.text;
  }
  if (inline.type === 'link') {
    return inlineToText(inline.content);
  }
  // tableContent → flatten rows; cells are arrays of inline content.
  if (inline.type === 'tableContent' && Array.isArray(inline.rows)) {
    return inline.rows
      .map((row) => {
        const cells = isRecord(row) && Array.isArray(row.cells) ? row.cells : [];
        return cells.map((cell) => inlineToText(cell)).join(' | ');
      })
      .join('\n');
  }
  // Fallback: a nested content array on an unknown node.
  if ('content' in inline) {
    return inlineToText(inline.content);
  }
  return '';
}

function indent(depth: number): string {
  return depth > 0 ? '  '.repeat(depth) : '';
}

function clampLevel(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) {
    return 1;
  }
  return Math.min(6, Math.max(1, Math.trunc(n)));
}

function stableFileTarget(
  props: Record<string, unknown>,
  options: MarkdownSerializeOptions | undefined,
): string | null {
  if (options?.fileRefs !== 'stable') {
    return null;
  }
  return typeof props.fileId === 'string' && props.fileId ? fileRefUrl(props.fileId) : null;
}

/** Serialize one block to a single Markdown line (or fenced block), indented by `depth`. */
export function blockToMarkdown(
  block: SerializableBlock,
  options?: MarkdownSerializeOptions,
): string {
  const depth = block.depth ?? 0;
  const pad = indent(depth);
  const props = readProps(block.content);
  const inline = readInline(block.content);
  const text = inlineToText(inline);

  switch (block.type) {
    case 'paragraph':
      return `${pad}${text}`;
    case 'heading':
      return `${pad}${'#'.repeat(clampLevel(props.level))} ${text}`;
    case 'bulletListItem':
      return `${pad}- ${text}`;
    case 'numberedListItem':
      return `${pad}1. ${text}`;
    case 'checkListItem':
      return `${pad}- [${props.checked === true ? 'x' : ' '}] ${text}`;
    case 'quote':
      return `${pad}> ${text}`;
    case 'codeBlock': {
      const lang = typeof props.language === 'string' ? props.language : '';
      return `${pad}\`\`\`${lang}\n${text}\n${pad}\`\`\``;
    }
    case 'divider':
      return `${pad}---`;
    case 'image': {
      const label =
        (typeof props.caption === 'string' && props.caption) ||
        (typeof props.name === 'string' && props.name) ||
        (typeof props.fileId === 'string' && props.fileId) ||
        'image';
      const target =
        stableFileTarget(props, options) ??
        (typeof props.name === 'string' ? props.name : (props.fileId ?? ''));
      return `${pad}![${label}](${target})`;
    }
    case 'file': {
      const name = typeof props.name === 'string' ? props.name : 'file';
      const target = stableFileTarget(props, options);
      return target ? `${pad}[${name}](${target})` : `${pad}[file: ${name}]`;
    }
    case 'database':
      return `${pad}[database: ${block.title?.trim() || 'Untitled'}]`;
    case 'page':
      return `${pad}[page: ${block.title?.trim() || 'Untitled'}]`;
    default:
      return `${pad}${text}`;
  }
}

/** Serialize an ordered list of blocks (each may carry its own `depth`) to Markdown. */
export function blocksToMarkdown(
  blocks: SerializableBlock[],
  options?: MarkdownSerializeOptions,
): string {
  return blocks
    .map((block) => blockToMarkdown(block, options))
    .filter((line) => line.trim().length > 0)
    .join('\n');
}
